import React, { useState, useEffect } from 'react';
import { baseConfig } from '../config/settings';
import tokenABI from '../abi/ADBYTES.json';
import { readWithFailover, getWalletWeb3, getAccount } from '../utils/web3';
import { subscribeWalletEvents } from '../utils/walletProvider';
import { fromWei, formatBalance } from '../utils/format';

const POLLING_INTERVAL = 30000;

const MyBalance = () => {
  const [tokenName, setTokenName] = useState('');
  const [erc20Balance, setErc20Balance] = useState('0');
  const [nativeBalance, setNativeBalance] = useState('0');

  useEffect(() => {
    let pollingInterval;

    const loadBalances = async () => {
      const walletWeb3 = getWalletWeb3();
      if (!walletWeb3) return;

      const account = await getAccount(walletWeb3);
      if (!account) return;

      try {
        // Read balances via public Base RPC — works even if wallet network detection is flaky.
        const { name, balance, nativeBalance: nativeWei } = await readWithFailover(
          async (readWeb3) => {
            const tokenContract = new readWeb3.eth.Contract(
              tokenABI,
              baseConfig.contractAddress
            );
            const [tokenBalance, native] = await Promise.all([
              tokenContract.methods.balanceOf(account).call(),
              readWeb3.eth.getBalance(account),
            ]);
            let tokenName = '';
            try {
              tokenName = await tokenContract.methods.name().call();
            } catch {
              /* name is optional */
            }
            return {
              name: tokenName,
              balance: fromWei(tokenBalance, readWeb3),
              nativeBalance: fromWei(native, readWeb3),
            };
          }
        );

        if (name) setTokenName(name);
        setErc20Balance(formatBalance(balance));
        setNativeBalance(formatBalance(nativeWei));
      } catch (error) {
        console.error('Failed to load balances:', error);
      }
    };

    const pollIfVisible = () => {
      if (!document.hidden) loadBalances();
    };

    loadBalances();
    pollingInterval = setInterval(pollIfVisible, POLLING_INTERVAL);
    document.addEventListener('visibilitychange', pollIfVisible);

    const unsubscribeWallet = subscribeWalletEvents({
      onAccountsChanged: loadBalances,
      onChainChanged: loadBalances,
    });

    return () => {
      unsubscribeWallet();
      document.removeEventListener('visibilitychange', pollIfVisible);
      clearInterval(pollingInterval);
    };
  }, []);

  const buySellLink = `https://app.uniswap.org/swap?chain=base&inputCurrency=${baseConfig.usdcAddress}&outputCurrency=${baseConfig.contractAddress}`;

  return (
    <div className="bg-white shadow-xl rounded-2xl p-6 border border-gray-100 flex flex-col justify-between">
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-6">My Balance</h3>

        <div className="space-y-4 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-xl p-4 border border-blue-100">
            <div className="text-xs text-gray-600 mb-1">ADBYTES Balance</div>
            <div className="text-3xl font-bold text-gray-900">{erc20Balance}</div>
            <div className="text-xs text-gray-500 mt-1">{tokenName}</div>
          </div>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="text-xs text-gray-600 mb-1">{baseConfig.nativeCurrency.symbol} Balance</div>
            <div className="text-2xl font-bold text-gray-900">{nativeBalance}</div>
            <div className="text-xs text-gray-500 mt-1">{baseConfig.nativeCurrency.name}</div>
          </div>
        </div>
      </div>

      <div>
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 space-y-1">
          <p className="text-xs text-blue-800">
            Balance does not include unclaimed platform rewards or staked ADBYTES.
          </p>
          <p className="text-xs text-blue-700">
            Staked positions and rewards are shown in the Staking section below.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <a
            href="/publisher/redeem"
            className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-3 rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all font-semibold text-sm text-center shadow-md hover:shadow-lg"
          >
            Claim Rewards
          </a>
          <a
            href={buySellLink}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-green-500 to-green-600 text-white px-4 py-3 rounded-lg hover:from-green-600 hover:to-green-700 transition-all font-semibold text-sm text-center shadow-md hover:shadow-lg"
          >
            Buy & Sell
          </a>
        </div>
      </div>
    </div>
  );
};

export default MyBalance;
